import { yupResolver } from "@hookform/resolvers/yup";
import { ChevronLeftIcon, EnterIcon } from "@radix-ui/react-icons";
import { AlertDialog, Box, Button, Card, Flex, Spinner, Text, TextField } from "@radix-ui/themes";
import { debounce, isEmpty } from "lodash";
import { useCallback, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useMutation } from "react-query";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import DateFieldComponent from "../../../components/inputs/Datefield/DatefieldComponent";
import RadioGroupComponent from "../../../components/inputs/RadioGroup/RadioGroupComponent";
import MultiSelectComponent from "../../../components/inputs/Select/MultiSelectComponent";
import SelectSearchableComponent from "../../../components/inputs/Select/SelectSearchableComponent";
import TextFieldComponent from "../../../components/inputs/Textfield/TextfieldComponent";
import { useAppDispatch } from "../../../hooks/hooks";
import { useGetRolesQuery } from "../../../hooks/useGetRolesQuery";
import { resetUserData } from "../../../redux/slices/users/user";
import { RootState } from "../../../redux/store";
import { routes } from "../../../routes";
import { addUserRole } from "../../../services/user/addUserRole";
import {
  createUser,
  getUserNameSuggestion,
} from "../../../services/user/createUser";
import { removeUserRole } from "../../../services/user/removeUserRole";
import { updateUser } from "../../../services/user/updateUser";
import styles from "../../../styles/CreateUser.module.scss";
import { UserType } from "../../../types/User";
import { Countries, Country } from "../../../utils/codes";
import { stringToBoolean } from "../../../utils/utilities";
import { Create_Edit_UserFormValidation } from "./Create_Edit_UserFormValidation";

export const Create_Edit_User = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isUpdateRoute = location.pathname === routes.updateUser;

  const dispatch = useAppDispatch();
  const defaultValues = useSelector((state: RootState) => state.user);
  const [showChangesNotification, setShowChangesNotification] = useState(false);

  const mutation = useMutation({
    mutationFn: defaultValues.id && isUpdateRoute ? updateUser : createUser,
    mutationKey: "create_edit_User",
  });


  const {
    data: roles,
    isLoading: isLoadingRoles,
    isError: isErrorRoles,
  } = useGetRolesQuery({ PageNumber: 1, PageSize: 400 });
  const {
    mutateAsync: mutationRemoveUserRole,
  } = useMutation({
    mutationKey: "removeUserRole",
    mutationFn: removeUserRole,
  });
  const {
    mutateAsync: mutationAddUserRole,
    isLoading: isLoadingMutationAddUserRole,
  } = useMutation({ mutationKey: "addUserRole", mutationFn: addUserRole });

  const {
    control,
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
    watch,
  } = useForm<UserType>({
    resolver: yupResolver(Create_Edit_UserFormValidation),
    defaultValues,
    mode: "onBlur",
    reValidateMode: "onBlur",
    shouldUseNativeValidation: false,
  });

  const onSubmit = handleSubmit(async (data) => {
    try {
      data.twoFactorEnabled = stringToBoolean(data.twoFactorEnabled);
      data.isForeign = stringToBoolean(data.isForeign);
      const result = await mutation.mutateAsync(data);
      if (!result) throw "Erro a realizar esta ação";
      try {
        const userId =
          JSON.parse(JSON.stringify(result)).id! ?? defaultValues?.id;
        if (!isEmpty(defaultValues.id)) {
          let rolesToDelete = defaultValues.roleIds;
          if (rolesToDelete && rolesToDelete.length > 0) {
            for (let index = 0; index < rolesToDelete.length; index++) {
              const ri = rolesToDelete[index];
              await mutationRemoveUserRole({
                roleId: ri,
                userId: userId,
              });
            }
          }
        }
        if (data?.roleIds && data?.roleIds.length > 0) {
          for (let index = 0; index < data?.roleIds.length; index++) {
            const ri = data?.roleIds[index];
            await mutationAddUserRole({
              roleId: ri,
              userId: userId,
            });
          }
        }
        toast.success(
          `Utilizador ${defaultValues.id && isUpdateRoute ? "atualizado" : "registado"
          }.`
        );
        setShowChangesNotification(true);

      } catch (error) {
        console.log({ error });
      }
    } catch (err: any) {
      toast.error(`${mutation.error ?? err?.message}`);
    }
  });

  const handleBack = () => {
    dispatch(resetUserData());
    navigate(routes.users);
  };

  const watchIsForeign = watch("isForeign");

  const filteredCountries = Countries.filter(
    (country: Country) =>
      !stringToBoolean(watchIsForeign) || country.code !== "MOZ"
  );

  const handleEmailChange = (e: any) => {
    var email = e.target.value;
    setValue("email", email);
  };
  const handleSecondaryEmailChange = (e: any) => {
    var secondaryEmail = e.target.value;
    setValue("secondaryEmail", secondaryEmail);
  };
  const fetchUserNameSuggestion = async (fullName: string) => {
    if (!fullName) return;
    try {
      const response = await getUserNameSuggestion({ name: fullName });
      setValue("userName", response);

    } catch (error: any) {

    } finally {

    }
  };

  const debouncedFetchUserName = useCallback(
    debounce(fetchUserNameSuggestion, 500),
    []
  );

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    setValue("name", name);
    !isUpdateRoute && debouncedFetchUserName(name);
  };

  const handleOnChangeIsForeign = (e: any) => {
    const isIsForeign = stringToBoolean(getValues(e.target.value));
    if (isIsForeign) {
      setValue("countryDescription", "");
    }
    if (!isIsForeign) {
      setValue("countryDescription", "MOZ");
    }
  };

  const handleBIChange = (e: any) => {
    var bi = e.target.value;
    setValue("bi", bi);
  };

  return (
    <Card size="3">
      <form onSubmit={onSubmit} noValidate>
        <Flex direction="column" gap="4">
          <Flex gap="2">
            <TextFieldComponent
              name="name"
              label="Nome"
              control={control}
              errors={errors}
              fieldProps={{
                placeholder: "Nome completo",
                onChange: handleNameChange,
              }}
              style={{ width: "100%" }}
              required={true}
            />
            <TextFieldComponent
              name="userName"
              label="Utilizador"
              control={control}
              errors={errors}
              fieldProps={{
                placeholder: "Utilizador",
                disabled: "disabled",
              }}
              style={{ width: "100%" }}
              required={true}
            />
            <TextFieldComponent
              name="email"
              label="Email"
              control={control}
              errors={errors}
              fieldProps={{
                placeholder: "seu@email.com",
                onChange: handleEmailChange,
                type: "email",
              }}
              style={{ width: "100%" }}
              required={true}
            />
            <TextFieldComponent
              name="secondaryEmail"
              label="Email alternativo"
              control={control}
              errors={errors}
              fieldProps={{
                placeholder: "seu@email.com",
                onChange: handleSecondaryEmailChange,
                type: "email",
              }}
              style={{ width: "100%" }}
            />
          </Flex>
          <Box style={{ width: "100%" }}>
            <Text as="label" size="2" className={styles.label}>
              <span>
                {stringToBoolean(watchIsForeign)
                  ? "Documento de identificação"
                  : "BI/DIRE/Cartão de eleitor/Carta de condução/Etc"}{" "}
                <span style={{ color: "red" }}>*</span>
              </span>
            </Text>
            <Controller
              name="bi"
              control={control}
              render={({ field }) => (
                <TextField.Root
                  size="2"
                  {...field}
                  placeholder={
                    stringToBoolean(watchIsForeign)
                      ? "Documento de identificação"
                      : "BI/DIRE/Cartão de eleitor/Carta de condução"
                  }
                  onChange={handleBIChange}
                />
              )}
            />
            {errors.bi && (
              <Text color="red" size="1">
                {errors.bi.message}
              </Text>
            )}
          </Box>
          <Flex gap="2">

            <TextFieldComponent
              name="bi"
              label="BI"
              control={control}
              errors={errors}
              fieldProps={{ placeholder: "BI" }}
              style={{ width: "100%" }}
              required={true}
            />
            <TextFieldComponent
              name="nuit"
              label="NUIT"
              control={control}
              errors={errors}
              fieldProps={{ placeholder: "Seu NUIT" }}
              style={{ width: "100%" }}
            />
            <TextFieldComponent
              name="phoneNumber"
              label="Telefone"
              control={control}
              errors={errors}
              fieldProps={{
                placeholder: "Ex: 82xxxxxxx"
              }}
              style={{ width: "100%" }}
              required={true}
            />

            <TextFieldComponent
              name="secondaryPhoneNumber"
              label="Telefone alternativo"
              control={control}
              errors={errors}
              fieldProps={{
                placeholder: "82xxxxxxx"
              }}
              style={{ width: "100%" }}
            />
          </Flex>
          <Flex gap="2">
            <DateFieldComponent
              name="dateOfBirth"
              label="Data de Nascimento"
              control={control}
              errors={errors}
              style={{ width: "100%" }}
              required={true}
            />
            <RadioGroupComponent
              name="gender"
              label="Gênero"
              control={control}
              errors={errors}
              style={{ width: "100%" }}
              options={[
                { value: "M", label: "Masculino" },
                { value: "F", label: "Feminino" },
              ]}
              fieldProps={{
                style: { display: "flex", flexDirection: "row", gap: "16px" },
              }}
            />
            {defaultValues.id && isUpdateRoute && (
              <RadioGroupComponent
                name="twoFactorEnabled"
                label="Autenticação de 2 fatores?"
                control={control}
                errors={errors}
                style={{ width: "100%" }}
                options={[
                  { value: "true", label: "Sim" },
                  { value: "false", label: "Não" },
                ]}
                fieldProps={{
                  style: { display: "flex", flexDirection: "row", gap: "16px" },
                }}
              />
            )}
          </Flex>
          <Flex gap="2">
            <MultiSelectComponent
              name="roleIds"
              label="Perfis"
              control={control}
              errors={errors}
              style={{ width: "100%" }}
              options={roles?.items.map((role) => ({
                value: role.name ?? "",
                label: role.name ?? "",
              }))}
              itemsError={isErrorRoles}
              itemsLoading={isLoadingRoles}
            />
          </Flex>
          <Flex gap="2">
            <RadioGroupComponent
              name="isForeign"
              label="Estrangeiro"
              control={control}
              errors={errors}
              style={{ width: "50%" }}
              options={[
                { value: "true", label: "Sim" },
                { value: "false", label: "Não" },
              ]}
              fieldProps={{
                style: { display: "flex", flexDirection: "row", gap: "16px" },
                onChange: handleOnChangeIsForeign,
              }}
            />
            <SelectSearchableComponent
              name="countryDescription"
              label="País"
              control={control}
              errors={errors}
              options={filteredCountries?.map((country: Country) => ({
                value: country.code,
                label: country.label ?? "",
              }))}
              style={{ width: "100%" }}
              fieldProps={{ disabled: !stringToBoolean(watchIsForeign) }}
            />
          </Flex>

          <Flex direction="row" gap="4" style={{ alignSelf: "end" }}>
            <Button
              color="red"
              onClick={handleBack}
              disabled={mutation.isLoading}
            >
              <ChevronLeftIcon /> voltar
            </Button>

            <Button
              type="submit"
              disabled={mutation.isLoading}
              variant="surface"
            >
              {mutation.isLoading ? (
                <Spinner loading>
                  {" "}
                  <EnterIcon />
                </Spinner>
              ) : (
                <EnterIcon />
              )}
              {defaultValues.id && isUpdateRoute ? "Atualizar" : "Registar"}
            </Button>
          </Flex>
        </Flex>
      </form>
      {showChangesNotification && (
        <AlertDialog.Root open={showChangesNotification} onOpenChange={() => {
          setShowChangesNotification(false)
          handleBack();
        }}>
          <AlertDialog.Content maxWidth="1000px">
            <AlertDialog.Title>Alterações salvas</AlertDialog.Title>
            <AlertDialog.Description>
              As alterações feitas terão efeito após o próximo login do utilizador.
            </AlertDialog.Description>
            <Flex gap="3" justify="center" mt="4">
              <AlertDialog.Cancel>
                <Button variant="soft" color="orange">
                  Entendido
                </Button>
              </AlertDialog.Cancel>
            </Flex>
          </AlertDialog.Content>
        </AlertDialog.Root>
      )}
    </Card>
  );
};